import { PageHeader } from "@/components/PageHeader";
import { Notify } from "@/components/ui/notify";
import { useAuthStore } from "@/stores/auth.store";
import { OrganizationIdentitySection } from "./OrganizationIdentitySection";
import { OperatingCalendarSection } from "./OperatingCalendarSection";
import { HolidaysSection } from "./HolidaysSection";
import { OrganizationRecurrenceSection } from "./OrganizationRecurrenceSection";
import { PoliciesSection } from "./policies/PoliciesSection";
import { IndicatorsSection } from "./indicators";
import { BatchCharacteristicsSection } from "./batch-characteristics";
import { TargetsSection } from "./targets";
import { useOrganization, useOrganizationCalendar } from "./hooks";

// Orquestrador da tela de organização: queries + composição, zero lógica de
// domínio (guardrail #1). As seções ficam AO LADO, não em `components/`.

export function OrganizationPage() {
  const isGestor = useAuthStore((s) => s.user?.role === "GESTOR");
  const orgQuery = useOrganization();
  const calendarQuery = useOrganizationCalendar();

  // ERRO DE LEITURA É TRATADO ANTES DE QUALQUER DECISÃO ENTRE VAZIO E COM DADOS.
  // Sem isto, uma falha de fetch renderiza `calendar: undefined` e a tela conclui "a fábrica não
  // declarou expediente" — oferecendo o estado vazio, com a semana sugerida pré-preenchida, a uma
  // fábrica que tem expediente cadastrado. O gestor salvaria por cima de um cadastro que ele nunca
  // chegou a ver. É a diferença entre uma tela feia e uma tela que mente.
  if (orgQuery.isError || calendarQuery.isError) {
    return (
      <div className="space-y-4">
        <PageHeader title="Organização" subtitle="Expediente, feriados e fuso da fábrica." />
        <Notify.QueryError
          error={orgQuery.error ?? calendarQuery.error}
          onRetry={() => {
            void orgQuery.refetch();
            void calendarQuery.refetch();
          }}
          title="Não foi possível carregar os dados da fábrica"
        />
      </div>
    );
  }

  const organization = orgQuery.data;
  const calendarData = calendarQuery.data;
  const loading = orgQuery.isLoading || calendarQuery.isLoading;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Organização"
        subtitle="O expediente padrão da fábrica, os feriados, o fuso horário e a política de julgamento dos indicadores. As linhas que não declaram expediente próprio seguem o que estiver aqui."
      />

      {loading || !organization || !calendarData ? (
        <p className="text-[13px] text-text-secondary">Carregando dados da fábrica…</p>
      ) : (
        <>
          <OrganizationIdentitySection organization={organization} canEdit={isGestor} />
          <OperatingCalendarSection
            // Remonta quando a linha divisória do rascunho muda — "há turnos declarados?" — para
            // re-semear a semana sugerida. Chavear por "há calendário?" remontava também quando só
            // os feriados eram carregados, e aí a semana sugerida era descartada sem ninguém pedir.
            key={
              (calendarData.calendar?.weeklyShifts.length ?? 0) > 0 ? "com-turnos" : "sem-turnos"
            }
            calendar={calendarData.calendar}
            inheritingLineCount={calendarData.inheritingLineCount}
            canEdit={isGestor}
          />
          <HolidaysSection calendar={calendarData.calendar} canEdit={isGestor} />
          <OrganizationRecurrenceSection calendar={calendarData.calendar} canEdit={isGestor} />
          {/* Spec 082 — a meta de período, de onde a meta diária de cada execução é derivada. */}
          <TargetsSection canEdit={isGestor} />
          {/* Spec 082 — os indicadores próprios da organização. Antes das políticas porque são
              consultados com mais frequência: uma fórmula errada aparece na tela todo dia. */}
          <IndicatorsSection canEdit={isGestor} />
          {/* Spec 084 — o que uma batelada carrega desde a entrada. Logo depois dos indicadores,
              que são o lugar onde a marca vira decisão (recorte e quadro comparativo). */}
          <BatchCharacteristicsSection canEdit={isGestor} />
          {/* Spec 079 — a política de julgamento da fábrica. Por último de propósito: quem abre
              esta tela quase sempre vem pelo expediente, e os dez limiares são configuração que se
              mexe raramente. */}
          <PoliciesSection organization={organization} canEdit={isGestor} />
        </>
      )}
    </div>
  );
}
